import { useEffect, useMemo, useState } from 'react';
import { useCardsStore } from '../stores/cards';
import { useCircleStore } from '../stores/circle';
import { useAuthStore } from '../stores/auth';
import { CardGrid } from '../components/cards/CardGrid';
import { ActiveCard } from '../components/cards/ActiveCard';
import { SendSheet } from '../components/cards/SendSheet';
import { EmptyState } from '../components/ui/EmptyState';
import type { CardTemplate, SentCard } from '@nudge/shared';

export function Home() {
  const circle = useCircleStore((s) => s.circle);
  const me = useAuthStore((s) => s.user);
  const templates = useCardsStore((s) => s.templates);
  const activeCards = useCardsStore((s) => s.activeCards);
  const fetchDeck = useCardsStore((s) => s.fetchDeck);
  const fetchActive = useCardsStore((s) => s.fetchActive);
  const [picked, setPicked] = useState<CardTemplate | null>(null);

  useEffect(() => {
    if (!circle) return;
    void fetchDeck(circle.id);
    void fetchActive(circle.id);
  }, [circle, fetchDeck, fetchActive]);

  const sortedActive = useMemo(() => {
    const list = [...activeCards];
    list.sort((a, b) => {
      if (a.intensity !== b.intensity) return a.intensity === 'firm' ? -1 : 1;
      return b.created_at.localeCompare(a.created_at);
    });
    return list;
  }, [activeCards]);

  // Single reduce instead of two filter passes over the same array
  const { partnerCards, mySent } = useMemo(() =>
    sortedActive.reduce<{ partnerCards: SentCard[]; mySent: SentCard[] }>(
      (acc, c) => {
        (c.sender_id === me?.id ? acc.mySent : acc.partnerCards).push(c);
        return acc;
      },
      { partnerCards: [], mySent: [] }
    ), [sortedActive, me?.id]);

  return (
    <div className="space-y-6">
      <section>
        <h2 className="mb-2 px-1 text-sm font-medium uppercase tracking-wide text-text-muted">From your circle</h2>
        {partnerCards.length === 0 ? (
          <EmptyState emoji="🌿" title="Nothing right now" body="When someone in your circle sends a card, it'll show up here." />
        ) : (
          <div className="space-y-2">
            {partnerCards.map((c) => <ActiveCard key={c.id} card={c} />)}
          </div>
        )}
      </section>

      {mySent.length > 0 && (
        <section>
          <h2 className="mb-2 px-1 text-sm font-medium uppercase tracking-wide text-text-muted">You sent</h2>
          <div className="space-y-2">
            {mySent.map((c) => <ActiveCard key={c.id} card={c} />)}
          </div>
        </section>
      )}

      <section>
        <h2 className="mb-2 px-1 text-sm font-medium uppercase tracking-wide text-text-muted">Send a card</h2>
        <CardGrid templates={templates} onPick={setPicked} />
      </section>

      <SendSheet template={picked} onClose={() => setPicked(null)} />
    </div>
  );
}
